/**
 * Event-day check-in: badge QR scanner + operator kiosk.
 * RPCs from 20261005000002_events_session_checkin.sql.
 */

import { supabase } from '@/supabase.config';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type CheckinResultCode =
  | 'OK'
  | 'ALREADY'
  | 'NEEDS_CONFIRMATION'
  | 'REJECTED'
  | 'EVENT_NOT_ACTIVE'
  | 'NOT_FOUND'
  | 'WRONG_EVENT';

export type CheckinWarning = 'NOT_VERIFIED' | 'UNPAID';
export type CheckinMethod = 'QR' | 'KIOSK' | 'MANUAL';

export interface CheckinParticipantInfo {
  id: string;
  full_name: string | null;
  dni: string | null;
  adb_code: string | null;
  district_name: string | null;
  region_name: string | null;
  kind: 'MINISTER' | 'DELEGATE' | 'GUEST';
  registration_status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  payment_status: string | null;
  calculated_price: number | null;
  public_token: string;
}

export interface CheckinResult {
  result: CheckinResultCode;
  participant?: CheckinParticipantInfo;
  warnings?: CheckinWarning[];
  checked_in_at?: string;
  rejection_reason?: string | null;
}

export interface CheckinSearchRow {
  participant_id: string;
  full_name: string | null;
  dni: string | null;
  adb_code: string | null;
  district_name: string | null;
  kind: 'MINISTER' | 'DELEGATE' | 'GUEST';
  registration_status: 'PRE_REGISTERED' | 'VERIFIED' | 'REJECTED';
  payment_status: string | null;
  public_token: string;
  event_checked_in_at: string | null;
  session_checked_in_at: string | null;
}

export interface AttendanceSummary {
  registered: number;
  registered_ministers: number;
  verified: number;
  event_attended: number;
  sessions: {
    session_id: string;
    name: string;
    session_date: string;
    start_time: string | null;
    attended: number;
    attended_ministers: number;
  }[];
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Badge QR holds `https://<domain>/p/<token>` — also accept a bare token. */
export function extractTokenFromQr(text: string): string | null {
  const match = text.match(/\/p\/([0-9a-f-]{36})/i) ?? text.match(UUID_RE);
  return match ? (match[1] ?? match[0]).toLowerCase() : null;
}

export const checkinByToken = (args: {
  token: string;
  eventId: string;
  sessionId: string | null;
  method?: CheckinMethod;
  force?: boolean;
}) =>
  rpc<CheckinResult>('checkin_by_token', {
    p_token: args.token,
    p_session_id: args.sessionId,
    p_method: args.method ?? 'QR',
    p_force: args.force ?? false,
    p_event_id: args.eventId || null,
  });

export const checkinParticipant = (args: {
  participantId: string;
  sessionId: string | null;
  method?: CheckinMethod;
  force?: boolean;
}) =>
  rpc<CheckinResult>('checkin_participant', {
    p_participant_id: args.participantId,
    p_session_id: args.sessionId,
    p_method: args.method ?? 'KIOSK',
    p_force: args.force ?? false,
  });

export const searchCheckinParticipants = (eventId: string, query: string, sessionId: string | null) =>
  rpc<CheckinSearchRow[]>('checkin_search', { p_event_id: eventId, p_query: query, p_session_id: sessionId });

export const getAttendanceSummary = (eventId: string) =>
  rpc<AttendanceSummary>('get_event_attendance_summary', { p_event_id: eventId });
